import React, { useMemo, useState } from 'react'
import Container from '@embracesbs/component-container'
import Text from '@embracesbs/component-text'
import Table from '@embracesbs/component-table'
import TableRow from '@embracesbs/component-tablerow'
import EmptyState from '@embracesbs/component-emptystate'
import { formatDateTime } from '../../Utilities/helpers/helpers'

interface CostPoolNamespace {
  namespace: string
  cpuShare: number
  memoryShare: number
  share: number
  cost: number
  cpuUsedShare?: number | null
  memoryUsedShare?: number | null
  usedShare?: number | null
  usedCost?: number | null
  slack?: number | null
}

interface CostClusterNamespace {
  namespace: string
  cost: number
  pools: string[]
  usedCost?: number | null
  slack?: number | null
}

interface CostPool {
  pool: string
  meters: string[]
  reservedHours: number
  onDemandHours: number
  spotHours: number
  reservationCost: number
  onDemandCost: number
  spotCost: number
  total: number
  // Only for the cluster the report was generated on, and only for pools Prometheus knows.
  namespaces?: CostPoolNamespace[] | null
  idleShare?: number
  idleCost?: number
  usedShare?: number | null
  usedCost?: number | null
}

interface CostCluster {
  name: string
  subscriptionId: string
  resourceGroup?: string | null
  pools: CostPool[]
  categories: Record<string, number>
  reservationCost: number
  total: number
  namespaces?: CostClusterNamespace[]
  computeAllocated?: number
  idleCost?: number
  usedCost?: number | null
}

interface CostConsumer {
  resource: string
  subscriptionId: string
  cluster?: string | null
  pool?: string | null
  meter: string
  hours: number
  share: number
  cost: number
}

interface CostReservation {
  reservationId?: string | null
  orderId?: string | null
  name: string
  sku?: string | null
  quantity?: number | null
  term?: string | null
  expires?: string | null
  billedTo?: string | null
  installment: number
  installmentEstimated?: boolean
  hoursUsed: number
  hoursAvailable?: number | null
  utilization?: number | null
  consumers: CostConsumer[]
}

interface CostSubscription {
  id: string
  name: string
  reservationsBilled: number
  reservationsConsumed: number
  difference: number
  vmOnDemand: number
  vmSpot: number
  clusters: number
}

interface CostOtherVm {
  resource: string
  subscriptionId: string
  resourceGroup: string
  reservedHours: number
  onDemandHours: number
  reservationCost: number
  onDemandCost: number
}

interface CostPeriod {
  id: string
  from: string
  to: string
  closed: boolean
  hours: number
  currency?: string | null
  subscriptions: CostSubscription[]
  clusters: CostCluster[]
  reservations: CostReservation[]
  otherVms: CostOtherVm[]
  warnings: string[]
}

interface CostReport {
  generatedAt: string
  generatedOn?: string
  subscriptions: Record<string, string>
  periods: CostPeriod[]
}

interface Column<T> {
  key: string
  header: string
  width: number
  tooltip?: string
  render: (row: T) => React.ReactNode
}

const money = (value: number | null | undefined, currency: string, digits = 0): string => {
  if (value === null || value === undefined || !Number.isFinite(value)) return '–'
  return new Intl.NumberFormat('nl-NL', {
    style: 'currency',
    currency,
    minimumFractionDigits: digits,
    maximumFractionDigits: digits
  }).format(value)
}

const hours = (value: number | null | undefined): string =>
  value === null || value === undefined ? '–' : `${Math.round(value).toLocaleString('nl-NL')} h`

const percent = (value: number | null | undefined): string => (value === null || value === undefined ? '–' : `${Math.round(value * 100)}%`)

const shortSubscription = (name: string): string => name.replace(/^Intercept Azure - /, '')

// One table per section; the embrace Table takes its column widths up front, so the
// section renders the header row itself the same way the KRR view does.
function Section<T>({
  title,
  subtitle,
  columns,
  rows,
  rowKey,
  trailing
}: {
  title: string
  subtitle?: React.ReactNode
  columns: Column<T>[]
  rows: T[]
  rowKey: (row: T) => string
  trailing?: React.ReactNode
}) {
  const minWidth = columns.reduce((sum, column) => sum + column.width, 0)
  return (
    <div style={{ marginBottom: '24px' }}>
      <Container justify='space-between' isFullWidth style={{ marginBottom: '8px', alignItems: 'baseline', flexWrap: 'wrap', gap: '12px' }}>
        <Text textStyle='h6' style={{ fontSize: '14px' }}>
          {title}
        </Text>
        {subtitle && (
          <Text textStyle='description' textColor='grey'>
            {subtitle}
          </Text>
        )}
      </Container>
      {rows.length === 0 ? (
        <Text textStyle='description' textColor='grey'>
          Nothing in this period.
        </Text>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <div style={{ minWidth: `${minWidth}px` }}>
            <Table isTableFixed columnStyle={columns.map((column) => ({ width: column.width, leftSpacing: 16 }))}>
              <TableRow>
                {columns.map((column) => (
                  <Text
                    key={column.key}
                    textStyle='description'
                    style={{
                      color: 'var(--tma-text-muted)',
                      fontWeight: 600,
                      fontSize: '10px',
                      letterSpacing: '0.02em',
                      textTransform: 'uppercase'
                    }}
                  >
                    <span data-tooltip-id='global-tooltip' data-tooltip-content={column.tooltip ?? column.header}>
                      {column.header}
                    </span>
                  </Text>
                ))}
              </TableRow>
              {rows.map((row) => (
                <TableRow key={rowKey(row)}>
                  {columns.map((column) => (
                    <React.Fragment key={column.key}>{column.render(row)}</React.Fragment>
                  ))}
                </TableRow>
              ))}
            </Table>
          </div>
        </div>
      )}
      {trailing}
    </div>
  )
}

const cell = (value: React.ReactNode, strong = false): React.ReactNode => (
  <Text style={{ fontVariantNumeric: 'tabular-nums', fontWeight: strong ? 600 : undefined }}>{value}</Text>
)

const CostReportView: React.FC<{ content: unknown }> = ({ content }) => {
  const report = useMemo<CostReport | null>(() => {
    if (content && typeof content === 'object' && Array.isArray((content as CostReport).periods)) return content as CostReport
    return null
  }, [content])

  const [periodId, setPeriodId] = useState<string | null>(null)

  if (!report) {
    return (
      <Text textColor='grey' align='center'>
        This report could not be parsed as a cost report.
      </Text>
    )
  }

  const period = report.periods.find((candidate) => candidate.id === periodId) ?? report.periods[report.periods.length - 1]
  if (!period) {
    return <EmptyState imageName='emptyfolder' size='medium' title='No periods' text='The report contains no billing periods.' />
  }

  const currency = period.currency ?? 'EUR'
  const subscriptionName = (id: string | null | undefined): string => (id ? shortSubscription(report.subscriptions[id] ?? id) : '–')

  const subscriptionColumns: Column<CostSubscription>[] = [
    { key: 'name', header: 'Subscription', width: 220, render: (row) => cell(shortSubscription(row.name), true) },
    {
      key: 'billed',
      header: 'Reservations billed',
      width: 170,
      tooltip: 'Monthly reservation installments invoiced to this subscription.',
      render: (row) => cell(money(row.reservationsBilled, currency))
    },
    {
      key: 'consumed',
      header: 'Reservations consumed',
      width: 190,
      tooltip: 'Installments allocated to this subscription by the hours its resources drew from each reservation.',
      render: (row) => cell(money(row.reservationsConsumed, currency))
    },
    {
      key: 'difference',
      header: 'Difference',
      width: 130,
      tooltip: 'Consumed minus billed. Positive: this subscription uses reservations bought on the other one.',
      render: (row) =>
        cell(
          <span
            style={{
              color: Math.abs(row.difference) < 1 ? undefined : row.difference > 0 ? 'var(--tma-status-failed)' : 'var(--tma-status-ok)'
            }}
          >
            {row.difference > 0 ? '+' : ''}
            {money(row.difference, currency)}
          </span>
        )
    },
    { key: 'onDemand', header: 'On-demand VMs', width: 140, render: (row) => cell(money(row.vmOnDemand, currency)) },
    { key: 'spot', header: 'Spot VMs', width: 110, render: (row) => cell(money(row.vmSpot, currency)) },
    {
      key: 'clusters',
      header: 'AKS clusters',
      width: 130,
      tooltip: 'Node resource groups plus allocated reservation cost, for the clusters in this subscription.',
      render: (row) => cell(money(row.clusters, currency), true)
    }
  ]

  const poolColumns: Column<CostPool>[] = [
    { key: 'pool', header: 'Pool', width: 150, render: (row) => cell(row.pool, true) },
    { key: 'sku', header: 'SKU', width: 170, render: (row) => cell(row.meters.join(', ')) },
    {
      key: 'reservedHours',
      header: 'Reserved',
      width: 110,
      tooltip: 'Node hours covered by a reservation.',
      render: (row) => cell(hours(row.reservedHours))
    },
    { key: 'onDemandHours', header: 'On-demand', width: 110, render: (row) => cell(hours(row.onDemandHours)) },
    { key: 'spotHours', header: 'Spot', width: 100, render: (row) => cell(hours(row.spotHours)) },
    {
      key: 'reservationCost',
      header: 'Reservation',
      width: 120,
      tooltip: 'Share of the monthly installments, by hours.',
      render: (row) => cell(money(row.reservationCost, currency))
    },
    { key: 'onDemandCost', header: 'On-demand', width: 120, render: (row) => cell(money(row.onDemandCost, currency)) },
    { key: 'spotCost', header: 'Spot', width: 100, render: (row) => cell(money(row.spotCost, currency)) },
    { key: 'total', header: 'Compute', width: 120, render: (row) => cell(money(row.total, currency), true) }
  ]

  const reservationColumns: Column<CostReservation>[] = [
    { key: 'name', header: 'Reservation', width: 230, render: (row) => cell(row.name, true) },
    { key: 'sku', header: 'SKU', width: 170, render: (row) => cell(row.sku ?? '–') },
    { key: 'quantity', header: 'Qty', width: 60, render: (row) => cell(row.quantity ?? '–') },
    { key: 'billedTo', header: 'Billed to', width: 150, render: (row) => cell(subscriptionName(row.billedTo)) },
    {
      key: 'installment',
      header: 'Installment',
      width: 120,
      tooltip:
        'Monthly amount on the invoice. A ≈ marks an estimate carried over from the previous month because this month has not been billed yet.',
      render: (row) => cell(`${row.installmentEstimated ? '≈ ' : ''}${money(row.installment, currency, 2)}`)
    },
    { key: 'hoursUsed', header: 'Hours used', width: 110, render: (row) => cell(hours(row.hoursUsed)) },
    {
      key: 'utilization',
      header: 'Utilization',
      width: 100,
      tooltip: 'Hours used over quantity times hours in the period. Reads low for the month a reservation was bought in.',
      render: (row) => cell(percent(row.utilization))
    },
    {
      key: 'consumers',
      header: 'Consumed by',
      width: 420,
      render: (row) =>
        cell(
          row.consumers.length === 0
            ? 'nothing'
            : row.consumers
                .map(
                  (consumer) =>
                    `${consumer.cluster && consumer.pool ? `${consumer.cluster}/${consumer.pool}` : consumer.resource} ${percent(consumer.share)}`
                )
                .join(' · ')
        )
    }
  ]

  const namespaceColumns = (cluster: CostCluster): Column<CostClusterNamespace>[] => {
    const shareOf = (value: number | null | undefined): string =>
      percent(cluster.computeAllocated && value !== null && value !== undefined ? value / cluster.computeAllocated : null)
    return [
      { key: 'namespace', header: 'Namespace', width: 280, render: (row) => cell(row.namespace, true) },
      { key: 'pools', header: 'Pools', width: 200, render: (row) => cell(row.pools.join(', ')) },
      {
        key: 'requested',
        header: 'Requested',
        width: 100,
        tooltip: "Share of the split node cost, by the CPU and memory requests of the namespace's running pods. This is what it pays.",
        render: (row) => cell(shareOf(row.cost))
      },
      {
        key: 'used',
        header: 'Used',
        width: 100,
        tooltip: 'The same share by actual CPU time and memory working set.',
        render: (row) => cell(shareOf(row.usedCost))
      },
      { key: 'cost', header: 'Compute', width: 120, render: (row) => cell(money(row.cost, currency), true) },
      {
        key: 'slack',
        header: 'Slack',
        width: 110,
        tooltip:
          'Requested minus used, in money: reserved capacity the service did not touch. Negative when it used more than it requested.',
        render: (row) =>
          cell(
            <span
              style={{ color: row.slack !== null && row.slack !== undefined && row.slack < -1 ? 'var(--tma-status-failed)' : undefined }}
            >
              {money(row.slack, currency)}
            </span>
          )
      }
    ]
  }

  const otherVmColumns: Column<CostOtherVm>[] = [
    { key: 'resource', header: 'VM', width: 220, render: (row) => cell(row.resource, true) },
    { key: 'subscription', header: 'Subscription', width: 150, render: (row) => cell(subscriptionName(row.subscriptionId)) },
    { key: 'resourceGroup', header: 'Resource group', width: 260, render: (row) => cell(row.resourceGroup) },
    { key: 'reservedHours', header: 'Reserved', width: 110, render: (row) => cell(hours(row.reservedHours)) },
    { key: 'onDemandHours', header: 'On-demand', width: 110, render: (row) => cell(hours(row.onDemandHours)) },
    { key: 'reservationCost', header: 'Reservation', width: 120, render: (row) => cell(money(row.reservationCost, currency)) },
    { key: 'onDemandCost', header: 'On-demand', width: 120, render: (row) => cell(money(row.onDemandCost, currency)) }
  ]

  return (
    <div>
      <Container justify='space-between' isFullWidth style={{ gap: '12px', marginBottom: '12px', flexWrap: 'wrap' }}>
        <Container style={{ gap: '18px', flexWrap: 'wrap' }}>
          <Text textStyle='description'>Generated: {formatDateTime(report.generatedAt) || report.generatedAt}</Text>
          {report.generatedOn && <Text textStyle='description'>From cluster: {report.generatedOn}</Text>}
          <Text textStyle='description'>
            Period: {period.from.slice(0, 10)} to {period.to.slice(0, 10)} {period.closed ? '' : '(month to date)'}
          </Text>
        </Container>
        <Container style={{ gap: '8px' }}>
          {report.periods.map((candidate) => {
            const isActive = candidate.id === period.id
            return (
              <button
                key={candidate.id}
                type='button'
                onClick={() => setPeriodId(candidate.id)}
                style={{
                  cursor: 'pointer',
                  border: '1px solid var(--tma-list-card-border)',
                  background: isActive ? 'var(--tma-text-strong)' : 'transparent',
                  color: isActive ? 'var(--tma-panel-bg)' : 'var(--tma-text-strong)',
                  borderRadius: '12px',
                  padding: '2px 10px',
                  fontSize: '12px',
                  fontWeight: 600
                }}
              >
                {candidate.id}
                {candidate.closed ? '' : ' (to date)'}
              </button>
            )
          })}
        </Container>
      </Container>

      {period.warnings.length > 0 && (
        <div style={{ marginBottom: '16px', padding: '8px 12px', border: '1px solid var(--tma-status-wait)', borderRadius: '6px' }}>
          {period.warnings.map((warning) => (
            <Text key={warning} textStyle='description' style={{ color: 'var(--tma-status-wait)' }}>
              {warning}
            </Text>
          ))}
        </div>
      )}

      <Section
        title='Per subscription'
        subtitle='Reservations are Shared scope: the installment lands on the subscription that bought the order, the hours wherever a matching VM runs.'
        columns={subscriptionColumns}
        rows={period.subscriptions}
        rowKey={(row) => row.id}
      />

      {period.clusters.map((cluster) => {
        const categories = Object.entries(cluster.categories)
          .filter(([category]) => category !== 'Virtual Machines')
          .sort(([, a], [, b]) => b - a)
        const unsplit = cluster.pools.filter((pool) => pool.namespaces === null).map((pool) => pool.pool)
        return (
          <React.Fragment key={`${cluster.subscriptionId}-${cluster.name}`}>
            <Section
              title={`${cluster.name} · ${subscriptionName(cluster.subscriptionId)}`}
              subtitle={`Total ${money(cluster.total, currency)}, of which reservations ${money(cluster.reservationCost, currency)}`}
              columns={poolColumns}
              rows={cluster.pools}
              rowKey={(row) => row.pool}
              trailing={
                categories.length > 0 && (
                  <Text textStyle='description' textColor='grey' style={{ marginTop: '6px' }}>
                    Besides nodes: {categories.map(([category, cost]) => `${category} ${money(cost, currency)}`).join(' · ')}
                  </Text>
                )
              }
            />
            {cluster.namespaces && cluster.namespaces.length > 0 && (
              <Section
                title={`${cluster.name} · per service`}
                subtitle={`Node cost of ${money(cluster.computeAllocated, currency)} split by what each namespace requested, with what it actually used next to it; what nothing requested is idle.`}
                columns={namespaceColumns(cluster)}
                rows={cluster.namespaces}
                rowKey={(row) => row.namespace}
                trailing={
                  <Text textStyle='description' textColor='grey' style={{ marginTop: '6px' }}>
                    Idle: {money(cluster.idleCost, currency)} (
                    {percent(cluster.computeAllocated ? (cluster.idleCost ?? 0) / cluster.computeAllocated : null)})
                    {cluster.usedCost !== null && cluster.usedCost !== undefined
                      ? ` · used in total: ${money(cluster.usedCost, currency)} (${percent(
                          cluster.computeAllocated ? cluster.usedCost / cluster.computeAllocated : null
                        )})`
                      : ''}
                    {unsplit.length > 0 ? ` · not split, no node data: ${unsplit.join(', ')}` : ''}
                  </Text>
                }
              />
            )}
          </React.Fragment>
        )
      })}

      <Section
        title='Reservations'
        subtitle='Monthly installments and who consumed the hours.'
        columns={reservationColumns}
        rows={period.reservations}
        rowKey={(row) => row.reservationId ?? row.orderId ?? row.name}
      />

      {period.otherVms.length > 0 && (
        <Section
          title='VMs outside AKS'
          subtitle='Virtual machines that draw on the same reservations or are billed on-demand.'
          columns={otherVmColumns}
          rows={period.otherVms}
          rowKey={(row) => `${row.subscriptionId}-${row.resource}`}
        />
      )}
    </div>
  )
}

export default CostReportView
